import { useMemo, useState } from 'react';
import Drawer from '@/components/base/Drawer';
import Badge from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import type { Appointment, Team } from '@/types';

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function durationToHours(duration: string): number {
  const m = duration.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 1;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

export default function TeamDetail({ team, onClose }: { team: Team; onClose: () => void }) {
  const { jobs, appointments, customers, addAppointment } = useAppStore();

  const teamJobs = jobs.filter((j) => j.team === team.name);
  const todayJobs = teamJobs.filter((j) => j.date === '26 Ağustos 2026');
  const upcoming = appointments.filter((a) => a.team === team.name && a.date !== '26 Ağustos 2026');
  const completed = teamJobs.filter((j) => j.status === 'Kapandı');

  // assignment form state
  const [customerName, setCustomerName] = useState('');
  const [date, setDate] = useState('26 Ağustos 2026');
  const [startTime, setStartTime] = useState('14:00');
  const [duration, setDuration] = useState('3 saat');
  const [conflict, setConflict] = useState<Appointment | null>(null);
  const [assigned, setAssigned] = useState(false);

  const conflictAppointments = useMemo(
    () => appointments.filter((a) => a.team === team.name),
    [appointments, team.name],
  );

  function checkConflict(): Appointment | null {
    const start = timeToMinutes(startTime);
    const end = start + durationToHours(duration) * 60;
    return (
      conflictAppointments.find((a) => {
        if (a.date !== date) return false;
        const aStart = timeToMinutes(a.startTime);
        const aEnd = aStart + durationToHours(a.duration) * 60;
        return start < aEnd && end > aStart;
      }) ?? null
    );
  }

  function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!customerName) return;
    const existing = checkConflict();
    if (existing) {
      setConflict(existing);
      setAssigned(false);
      return;
    }
    const customer = customers.find((c) => c.name === customerName);
    addAppointment({
      id: `RDV-2026-${String(Date.now()).slice(-4)}`,
      customerName,
      phone: customer?.phone ?? '',
      service: 'Villa Temizliği',
      location: customer?.region ?? '',
      address: customer?.address ?? '',
      date,
      startTime,
      duration,
      team: team.name,
      status: 'Planlandı',
      quoteAmount: 0,
      quoteId: '',
      customerNote: '',
      internalNote: '',
    });
    setConflict(null);
    setAssigned(true);
    setCustomerName('');
  }

  return (
    <Drawer open onClose={onClose} title={team.name} width="max-w-2xl">
      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-4">
          <div>
            <div className="text-sm font-semibold text-foreground-900">{team.status}</div>
            <div className="text-xs text-foreground-500">{team.location || 'Şu an iş yok'}</div>
          </div>
          <Badge tone={team.status === 'Müsait' ? 'green' : 'gold'}>{team.status}</Badge>
        </div>

        {/* Members */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Personel ({team.members.length})</h4>
          <div className="flex flex-wrap gap-2">
            {team.members.map((m) => (
              <span key={m} className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-white px-3 py-1.5 text-sm text-foreground-700">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                  {m.split(' ').map((w) => w[0]).join('')}
                </span>
                {m}
              </span>
            ))}
          </div>
        </div>

        {/* Today's jobs */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Bugünün İşleri</h4>
          {todayJobs.length === 0 ? (
            <p className="text-sm text-foreground-400">Bugün planlanmış iş bulunmuyor.</p>
          ) : (
            <ul className="space-y-2">
              {todayJobs.map((j) => (
                <li key={j.id} className="flex items-center justify-between rounded-lg border border-background-200 bg-white px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground-900">{j.customerName} — {j.service}</div>
                    <div className="text-xs text-foreground-500">{j.time} · {j.location}</div>
                  </div>
                  <Badge tone="navy">{j.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upcoming */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Yaklaşan İşler</h4>
          {upcoming.length === 0 ? (
            <p className="text-sm text-foreground-400">Yaklaşan iş yok.</p>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((a) => (
                <li key={a.id} className="flex items-center justify-between rounded-lg border border-background-200 bg-white px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground-900">{a.customerName} — {a.service}</div>
                    <div className="text-xs text-foreground-500">{a.date} · {a.startTime}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Completed count */}
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-sm">
          <i className="ri-check-double-line text-emerald-600" />
          <span className="font-semibold text-emerald-700">Tamamlanan iş sayısı: {completed.length}</span>
        </div>

        {/* Assign form */}
        <div className="rounded-lg border border-background-200 p-4">
          <h4 className="mb-3 font-heading text-sm font-bold text-foreground-950">Randevu Ata</h4>
          <form onSubmit={handleAssign} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelClass}>Müşteri</label>
                <select className={inputClass} value={customerName} onChange={(e) => setCustomerName(e.target.value)}>
                  <option value="">Müşteri seç</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Tarih</label>
                <input className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Başlangıç</label>
                <input className={inputClass} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Beklenen Süre</label>
                <select className={inputClass} value={duration} onChange={(e) => setDuration(e.target.value)}>
                  {['2 saat', '3 saat', '4 saat', '5 saat', '6 saat', '8 saat'].map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            {conflict && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <i className="ri-alert-line mt-0.5 text-base" />
                <span>
                  <strong>{team.name}</strong> {conflict.startTime} saatinde{' '}
                  <strong>{conflict.customerName}</strong> işine atanmış. Yine de atamak ister misiniz?
                </span>
              </div>
            )}
            {assigned && !conflict && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
                <i className="ri-checkbox-circle-line text-base" />
                Randevu başarıyla atandı.
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
              >
                Randevuyu Ata
              </button>
              {conflict && (
                <button
                  type="button"
                  onClick={() => {
                    const customer = customers.find((c) => c.name === customerName);
                    if (customer) {
                      addAppointment({
                        id: `RDV-2026-${String(Date.now()).slice(-4)}`,
                        customerName,
                        phone: customer.phone,
                        service: 'Villa Temizliği',
                        location: customer.region,
                        address: customer.address,
                        date,
                        startTime,
                        duration,
                        team: team.name,
                        status: 'Planlandı',
                        quoteAmount: 0,
                        quoteId: '',
                        customerNote: '',
                        internalNote: '',
                      });
                    }
                    setConflict(null);
                    setAssigned(true);
                    setCustomerName('');
                  }}
                  className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100"
                >
                  Yine de Ata
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Drawer>
  );
}